import React, { useState, useRef, useMemo, useCallback } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ChevronLeft,
  CircleUserRound,
  Minus,
  Tag,
  TriangleDashed,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { HalfFilledTriangleIcon } from "@/assets/icons/half-filled-triangle-icon";
import { defaultPriorities } from "@/types/priorities";
import { ESTIMATE_POINTS, getTShirtSize } from "@/types/issues";
import { columnStatuses } from "../../ProjectList/CanvasKanban";
import { UserAvatar } from "../../common/UserAvatar";
import { useIssuesStore } from "@/store/zustand/issues-store";
import type { Project } from "@/utils/types";
import type {
  ProjectUser,
  ProjectLabel,
  FilterState,
} from "../../ProjectList/types";
import { getProjectLabels, getProjectUsers } from "@/utils/typeGuards";
import {
  filterOptions,
  dueDateFilterOptions,
  FilterCount,
  calculateFilterCounts,
  createSelectedFiltersSet,
  updateFiltersFromSelected,
  filterMenuItems,
  scrollbarStyles,
} from "@/utils/filterUtils";
import { FilterType, DueDateFilterValue } from "@/types/filterEnums";

interface ViewsFilterMenuProps {
  className?: string;
  onOpenChange?: (open: boolean) => void;
  filterType: string;
  currentProject: Project | null;
  children: React.ReactNode;
  filterChipProps?: Record<string, unknown>;
  filters: FilterState;
  updateFilters: (filters: Partial<FilterState>) => void;
}

export const ViewsFilterMenu = ({
  className,
  onOpenChange,
  filterType,
  currentProject,
  children,
  filters,
  updateFilters,
}: ViewsFilterMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const labels = getProjectLabels(currentProject?.labels);
  const statuses = columnStatuses;

  const users = useMemo(() => {
    const defaultUsersList = getProjectUsers(currentProject?.users);

    const sortedUsersList = defaultUsersList.sort(
      (a: ProjectUser, b: ProjectUser) => {
        const aName = a.name?.toLowerCase() || a.email?.toLowerCase();
        const bName = b.name?.toLowerCase() || b.email?.toLowerCase();
        return aName.localeCompare(bName);
      },
    );
    return sortedUsersList;
  }, [currentProject]);

  const allIssues = useIssuesStore((state) =>
    Object.values(state.issuesByProject).flatMap((projectIssues) =>
      Object.values(projectIssues),
    ),
  );

  const filterCounts = useMemo(() => {
    if (!currentProject?.id) return null;

    const availableStatusNames = statuses.map((status) => status.name);

    return calculateFilterCounts(
      allIssues,
      currentProject.id,
      availableStatusNames,
    );
  }, [allIssues, currentProject?.id, statuses]);

  const assigneesWithIssues = useMemo(() => users, [users]);

  const unassignedCount = useMemo(
    () => filterCounts?.assignees[DueDateFilterValue.NONE] || 0,
    [filterCounts?.assignees],
  );

  const labelsWithIssues = useMemo(
    () =>
      labels.filter((label) => {
        return label && label.label && label.label.length > 0;
      }),
    [labels],
  );

  const noLabelCount = useMemo(
    () => filterCounts?.labels[DueDateFilterValue.NONE] || 0,
    [filterCounts?.labels],
  );

  // Convert filter state to selectedFilters Set for the UI
  const selectedFilters = useMemo(() => {
    if (!filters) return new Set<string>();

    return createSelectedFiltersSet(
      filters.statuses || [],
      filters.labels || [],
      filters.userIds || [],
      filters.estimates || [],
      filters.priorities || [],
      filters.dueDates || {
        none: false,
        overdue: false,
        today: false,
        thisWeek: false,
        nextWeek: false,
        later: false,
      },
    );
  }, [filters]);

  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleUpdateFiltersFromSelected = useCallback(
    (newSelectedFilters: Set<string>) => {
      if (!currentProject) {
        return;
      }
      updateFiltersFromSelected(
        newSelectedFilters,
        (projectId, newFilters) => {
          updateFilters(newFilters);
        },
        currentProject.id,
      );
    },
    [currentProject, updateFilters],
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
    }
    onOpenChange?.(open);
  };

  const renderFilterContent = useMemo(() => {
    const option = filterOptions.find((opt) => opt.id === filterType);

    const getMenuItems = () => {
      let items: JSX.Element[] = [];

      switch (option?.id) {
        case FilterType.STATUS: {
          items = statuses.map((status) => (
            <DropdownMenuCheckboxItem
              key={status.id}
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
              checked={selectedFilters.has(`status-${status.id}`)}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add(`status-${status.id}`);
                } else {
                  newSelectedFilters.delete(`status-${status.id}`);
                }
                handleUpdateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <div className="size-4 flex items-center justify-center">
                  {status.filterIcon ? <status.filterIcon /> : <status.icon />}
                </div>
                <span>{status.name}</span>
              </div>
              {filterCounts && (
                <FilterCount count={filterCounts.statuses[status.id] || 0} />
              )}
            </DropdownMenuCheckboxItem>
          ));
          break;
        }
        case FilterType.ASSIGNEE: {
          const showUnassigned = unassignedCount > 0;

          items = [
            ...(showUnassigned
              ? [
                  <DropdownMenuCheckboxItem
                    key="none"
                    className="flex items-center gap-2 select-none"
                    checked={selectedFilters.has("assignee-none")}
                    onCheckedChange={(checked) => {
                      const newSelectedFilters = new Set(selectedFilters);
                      if (checked) {
                        newSelectedFilters.add("assignee-none");
                      } else {
                        newSelectedFilters.delete("assignee-none");
                      }
                      handleUpdateFiltersFromSelected(newSelectedFilters);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <CircleUserRound className="size-5 text-secondary-n-600" />
                      <span>Unassigned</span>
                    </div>
                    <FilterCount count={unassignedCount} />
                  </DropdownMenuCheckboxItem>,
                ]
              : []),
            ...assigneesWithIssues.map((user) => (
              <DropdownMenuCheckboxItem
                key={user?.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`assignee-${user?.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`assignee-${user?.id}`);
                  } else {
                    newSelectedFilters.delete(`assignee-${user?.id}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <UserAvatar
                    userName={user?.name || user?.email}
                    userId={user?.id}
                  />
                  <span className="truncate max-w-[110px]">
                    {user?.name || user?.email}
                  </span>
                </div>
                <FilterCount count={filterCounts?.assignees[user?.id] || 0} />
              </DropdownMenuCheckboxItem>
            )),
          ];
          break;
        }
        case FilterType.PRIORITY: {
          items = [
            <DropdownMenuCheckboxItem
              key="none"
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
              checked={selectedFilters.has("priority-0")}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add("priority-0");
                } else {
                  newSelectedFilters.delete("priority-0");
                }
                handleUpdateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <Minus className="size-4 text-muted-foreground" />
                <span>No Priority</span>
              </div>
              {filterCounts && (
                <FilterCount count={filterCounts.priorities["0"] || 0} />
              )}
            </DropdownMenuCheckboxItem>,
            ...defaultPriorities.map((priority) => (
              <DropdownMenuCheckboxItem
                key={priority.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`priority-${priority.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`priority-${priority.id}`);
                  } else {
                    newSelectedFilters.delete(`priority-${priority.id}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <priority.icon className="size-4 text-muted-foreground" />
                  <span>{priority.name}</span>
                </div>
                {filterCounts && (
                  <FilterCount
                    count={filterCounts.priorities[priority.id.toString()] || 0}
                  />
                )}
              </DropdownMenuCheckboxItem>
            )),
          ];
          break;
        }
        case FilterType.LABEL: {
          const showNoLabel = noLabelCount > 0;

          items = [
            ...(showNoLabel
              ? [
                  <DropdownMenuCheckboxItem
                    key="none"
                    className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                    checked={selectedFilters.has("label-none")}
                    onCheckedChange={(checked) => {
                      const newSelectedFilters = new Set(selectedFilters);
                      if (checked) {
                        newSelectedFilters.add("label-none");
                      } else {
                        newSelectedFilters.delete("label-none");
                      }
                      handleUpdateFiltersFromSelected(newSelectedFilters);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Tag className="size-4 text-muted-foreground" />
                      <span>No label</span>
                    </div>
                    <FilterCount count={noLabelCount} />
                  </DropdownMenuCheckboxItem>,
                ]
              : []),
            ...labelsWithIssues.map((label) => (
              <DropdownMenuCheckboxItem
                key={label.id}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`label-${label.id}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`label-${label.id}`);
                  } else {
                    newSelectedFilters.delete(`label-${label.id}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="size-2.5 rounded-[3px] shrink-0"
                    style={{
                      backgroundColor: label.color?.value || "red",
                    }}
                  />
                  <span>{label.label}</span>
                </div>
                <FilterCount count={filterCounts?.labels[label.id] || 0} />
              </DropdownMenuCheckboxItem>
            )),
          ];
          break;
        }
        case FilterType.ESTIMATE: {
          items = [
            <DropdownMenuCheckboxItem
              key="none"
              className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
              checked={selectedFilters.has("estimate-none")}
              onCheckedChange={(checked) => {
                const newSelectedFilters = new Set(selectedFilters);
                if (checked) {
                  newSelectedFilters.add("estimate-none");
                } else {
                  newSelectedFilters.delete("estimate-none");
                }
                handleUpdateFiltersFromSelected(newSelectedFilters);
              }}
            >
              <div className="flex items-center gap-2">
                <TriangleDashed className="size-4 text-muted-foreground" />
                <span>No Estimate</span>
              </div>
              {filterCounts && (
                <FilterCount
                  count={filterCounts.estimates[DueDateFilterValue.NONE] || 0}
                />
              )}
            </DropdownMenuCheckboxItem>,
            ...ESTIMATE_POINTS.map((point) => (
              <DropdownMenuCheckboxItem
                key={point}
                className="flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                checked={selectedFilters.has(`estimate-${point}`)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(`estimate-${point}`);
                  } else {
                    newSelectedFilters.delete(`estimate-${point}`);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div className="flex items-center gap-2">
                  <HalfFilledTriangleIcon className="size-4 text-muted-foreground" />
                  <span>{getTShirtSize(point)}</span>
                </div>
                {filterCounts && (
                  <FilterCount
                    count={filterCounts.estimates[point.toString()] || 0}
                  />
                )}
              </DropdownMenuCheckboxItem>
            )),
          ];
          break;
        }
        case FilterType.DUE_DATE: {
          items = dueDateFilterOptions.map((option) => {
            return (
              <DropdownMenuCheckboxItem
                key={option.id}
                className={cn(
                  "flex items-center gap-2 select-none hover:bg-secondary-n-800 data-highlighted:bg-secondary-n-800",
                  option.className,
                )}
                checked={selectedFilters.has(option.id)}
                onCheckedChange={(checked) => {
                  const newSelectedFilters = new Set(selectedFilters);
                  if (checked) {
                    newSelectedFilters.add(option.id);
                  } else {
                    newSelectedFilters.delete(option.id);
                  }
                  handleUpdateFiltersFromSelected(newSelectedFilters);
                }}
              >
                <div
                  className="flex items-center gap-2"
                  style={{ color: option.className }}
                >
                  <option.icon className="size-4" />
                  <span>{option.label}</span>
                </div>
                {filterCounts && (
                  <FilterCount
                    count={
                      filterCounts.dueDates[option.id.replace("due-", "")] || 0
                    }
                  />
                )}
              </DropdownMenuCheckboxItem>
            );
          });
          break;
        }
        default: {
          items = [];
          break;
        }
      }

      return filterMenuItems(items, searchQuery);
    };

    return (
      <>
        <DropdownMenuItem
          className="flex items-center gap-2 cursor-pointer p-0"
          onClick={(e) => {
            e.preventDefault();
            setSearchQuery("");
            inputRef.current?.focus();
          }}
          onSelect={(e) => e.preventDefault()}
        >
          <div className="size-5 flex items-center justify-center">
            <ChevronLeft className="size-4 text-muted-foreground" />
          </div>
          <span>{option?.label}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="-mx-1" />
        <div
          className="max-h-[256px] overflow-y-auto filter-menu-scroll"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
          }}
        >
          <style>{scrollbarStyles}</style>
          {getMenuItems()}
        </div>
      </>
    );
  }, [
    filterType,
    selectedFilters,
    statuses,
    unassignedCount,
    assigneesWithIssues,
    filterCounts,
    noLabelCount,
    labelsWithIssues,
    searchQuery,
    handleUpdateFiltersFromSelected,
  ]);

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        ref={menuRef}
        align="start"
        className="w-56"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <div className="px-2 py-2 border-secondary-n-800">
          <input
            ref={inputRef}
            type="text"
            placeholder="Filter"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className="w-full bg-transparent text-sm border-0 outline-hidden focus:ring-0 p-0 placeholder:text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
            }}
            autoFocus
          />
        </div>
        <DropdownMenuSeparator />
        {renderFilterContent}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ViewsFilterMenu;
